import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type {
	LegacyTimeOfDay,
	TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import { resolveAmbientEffectTime } from "./ambientEffectClock";
import { getChimneySmokeLook, planChimneyVent } from "./chimneySmokeLayout";

/**
 * A thin plume of wood smoke from the chimney.
 *
 * All puffs are one draw call: a single geometry of camera-facing quads
 * whose whole life (rise, drift with the wind, growth, fade) is computed
 * in the vertex shader from one shared time uniform and a per-puff seed.
 * The fragment shader shapes each quad into a wisp with two octaves of
 * value noise that scroll upward, so no two puffs share a silhouette.
 *
 * Render contract as in ambientEffectClock.ts: `useFrame` only writes the
 * time uniform and never invalidates, so the plume drifts while the viewer
 * repaints anyway and holds still at rest.
 */

const PUFF_COUNT = 36;
/** Seconds from leaving the flue to fading out. */
const PUFF_LIFETIME_S = 9;
/** How high a puff climbs over its life before it has faded (m). */
const RISE_M = 2.1;
/** Prevailing wind in the rig's frame, metres of drift over a lifetime. */
const WIND_DRIFT: [number, number, number] = [0.95, 0, -0.35];

const VERTEX_SHADER = /* glsl */ `
uniform float uTime;
uniform float uRate;
uniform float uRise;
uniform float uVentRadius;
uniform vec3 uVent;
uniform vec3 uWind;
attribute vec2 aCorner;
attribute float aSeed;
varying vec2 vUv;
varying float vLife;
varying float vSeed;

void main() {
	float life = fract(uTime * uRate + aSeed);
	// Fast out of the flue, slowing as it cools and loses buoyancy.
	float rise = uRise * (1.0 - pow(1.0 - life, 1.7));
	vec3 wobble = vec3(
		sin(uTime * 0.61 + aSeed * 31.0),
		0.0,
		cos(uTime * 0.47 + aSeed * 17.0)
	) * 0.09 * life;
	vec3 centre = uVent + vec3(0.0, rise, 0.0) + uWind * pow(life, 1.35) + wobble;
	// Puffs swell fast so neighbours overlap into one continuous veil;
	// a bead-on-a-string column is what separate small puffs read as.
	float size = mix(uVentRadius * 2.6, 1.5, pow(life, 0.6));

	// Spin each puff a little so the noise never lines up between them.
	float angle = aSeed * 6.2831 + life * 0.8;
	vec2 corner = mat2(cos(angle), -sin(angle), sin(angle), cos(angle)) * aCorner;

	vec4 view = modelViewMatrix * vec4(centre, 1.0);
	view.xy += corner * size;
	gl_Position = projectionMatrix * view;

	vUv = aCorner + 0.5;
	vLife = life;
	vSeed = aSeed;
}
`;

const FRAGMENT_SHADER = /* glsl */ `
uniform float uTime;
uniform float uDensity;
uniform vec3 uColor;
varying vec2 vUv;
varying float vLife;
varying float vSeed;

float smokeHash(vec2 p) {
	return fract(sin(dot(p, vec2(41.23, 289.17))) * 15731.743);
}
float smokeNoise(vec2 p) {
	vec2 i = floor(p);
	vec2 f = fract(p);
	vec2 u = f * f * (3.0 - 2.0 * f);
	return mix(
		mix(smokeHash(i), smokeHash(i + vec2(1.0, 0.0)), u.x),
		mix(smokeHash(i + vec2(0.0, 1.0)), smokeHash(i + vec2(1.0, 1.0)), u.x),
		u.y
	);
}

void main() {
	vec2 p = vUv - 0.5;
	float r = length(p) * 2.0;
	float body = 1.0 - smoothstep(0.0, 1.0, r);
	body *= body;
	vec2 q = vUv * 2.6 + vec2(vSeed * 13.7, vSeed * 7.1 - uTime * 0.12);
	float n = smokeNoise(q) * 0.62 + smokeNoise(q * 2.3 + 4.1) * 0.38;
	// Denser, rounder near the flue; torn into wisps as it thins out.
	float tear = mix(0.05, 0.4, vLife);
	float wisp = smoothstep(tear, tear + 0.6, body * (0.35 + n * 1.1));
	float fadeIn = smoothstep(0.0, 0.07, vLife);
	float fadeOut = pow(1.0 - vLife, 1.4);
	float alpha = wisp * fadeIn * fadeOut * uDensity;
	// Breaks the 8-bit banding of a slow alpha ramp.
	alpha += (smokeHash(gl_FragCoord.xy) - 0.5) * 0.008;
	gl_FragColor = vec4(uColor, clamp(alpha, 0.0, 1.0));
}
`;

function createPuffGeometry(): THREE.BufferGeometry {
	const corners = [
		[-0.5, -0.5],
		[0.5, -0.5],
		[0.5, 0.5],
		[-0.5, 0.5],
	];
	const cornerData = new Float32Array(PUFF_COUNT * 8);
	const seedData = new Float32Array(PUFF_COUNT * 4);
	const index: number[] = [];
	for (let puff = 0; puff < PUFF_COUNT; puff += 1) {
		// Evenly staggered start times, jittered, so the column is
		// continuous instead of pulsing.
		const jitter = (Math.sin(puff * 91.7) * 0.5 + 0.5) * 0.35;
		const seed = (puff + jitter) / PUFF_COUNT;
		corners.forEach(([x, y], c) => {
			cornerData[(puff * 4 + c) * 2] = x;
			cornerData[(puff * 4 + c) * 2 + 1] = y;
			seedData[puff * 4 + c] = seed;
		});
		const base = puff * 4;
		index.push(base, base + 1, base + 2, base, base + 2, base + 3);
	}
	const geometry = new THREE.BufferGeometry();
	// `position` is unused by the shader but three needs it for the draw
	// range; every vertex sits at the origin and the shader places it.
	geometry.setAttribute(
		"position",
		new THREE.BufferAttribute(new Float32Array(PUFF_COUNT * 12), 3),
	);
	geometry.setAttribute("aCorner", new THREE.BufferAttribute(cornerData, 2));
	geometry.setAttribute("aSeed", new THREE.BufferAttribute(seedData, 1));
	geometry.setIndex(index);
	return geometry;
}

interface SceneChimneySmokeProps {
	enabled: boolean;
	/** Bumped after every dirty-tracked rebuild; the vent re-measures on it. */
	modelRevision: number;
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number;
}

export function SceneChimneySmoke({
	enabled,
	modelRevision,
	timeOfDay,
}: SceneChimneySmokeProps) {
	const scene = useThree((state) => state.scene);

	const vent = useMemo(() => {
		if (!enabled) return null;
		// `modelRevision` is the rebuild signal, not a value we read.
		void modelRevision;
		const rig = scene?.getObjectByName("sauna-rig") ?? null;
		return planChimneyVent(rig, rig);
	}, [enabled, scene, modelRevision]);

	const geometry = useMemo(() => (vent ? createPuffGeometry() : null), [vent]);
	useEffect(() => () => geometry?.dispose(), [geometry]);

	const look = useMemo(() => getChimneySmokeLook(timeOfDay), [timeOfDay]);

	// ONE uniforms object for the material's lifetime. R3F does not carry
	// a replaced `uniforms` object into an already-compiled ShaderMaterial
	// (see the 2026-07-15 sky-dome note): swapping it on a vent change left
	// the plume at the chimney of the design before the last rebuild.
	const uniforms = useMemo(
		() => ({
			uTime: { value: resolveAmbientEffectTime(0) },
			uRate: { value: 1 / PUFF_LIFETIME_S },
			uRise: { value: RISE_M },
			uVentRadius: { value: 0.08 },
			uVent: { value: new THREE.Vector3() },
			uWind: { value: new THREE.Vector3(...WIND_DRIFT) },
			uDensity: { value: 0 },
			uColor: { value: new THREE.Color() },
		}),
		[],
	);
	const invalidate = useThree((state) => state.invalidate);
	const materialRef = useRef<THREE.ShaderMaterial>(null);
	useEffect(() => {
		const material = materialRef.current;
		if (!material || !vent) return;
		material.uniforms.uVent.value.set(...vent.position);
		material.uniforms.uVentRadius.value = vent.radius;
		material.uniforms.uDensity.value = look.density;
		material.uniforms.uColor.value.copy(look.color);
		invalidate();
	}, [vent, look, invalidate]);
	useFrame((state) => {
		const material = materialRef.current;
		if (!material) return;
		material.uniforms.uTime.value = resolveAmbientEffectTime(
			state.clock.elapsedTime,
		);
	});

	if (!vent || !geometry) return null;

	return (
		<mesh
			name="chimneySmoke/plume"
			geometry={geometry}
			// The shader moves every vertex away from the origin-only
			// `position` attribute, so the geometry's bounds are meaningless.
			frustumCulled={false}
			renderOrder={3}
			raycast={() => null}
			dispose={null}
		>
			<shaderMaterial
				ref={materialRef}
				vertexShader={VERTEX_SHADER}
				fragmentShader={FRAGMENT_SHADER}
				uniforms={uniforms}
				transparent
				depthWrite={false}
			/>
		</mesh>
	);
}
